import { IconChevronLeft, IconChevronRight, IconChevronsLeft, IconChevronsRight } from "@tabler/icons-react";
import { cn } from "@/lib/cn";
import { Group } from "./Group";
import { Text } from "./Text";

export interface PaginadorTablaProps {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  sustantivo?: string;
  variante?: "superior" | "inferior";
}

/**
 * Reemplazo de `shared/ui/PaginadorTabla.tsx` (23 usos, convención P42) ·
 * `variante="superior"` (solo controles, alineados a la derecha, se oculta
 * con 1 sola página) va ARRIBA de la tabla; `variante="inferior"` (default,
 * con el texto "Mostrando X–Y de Z") va ABAJO.
 */
export function PaginadorTabla({ total, page, pageSize, onPageChange, sustantivo = "elementos", variante = "inferior" }: PaginadorTablaProps) {
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
  const desde = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const hasta = Math.min(page * pageSize, total);

  if (variante === "superior") {
    if (totalPaginas <= 1) return null;
    return (
      <Group justify="flex-end">
        <PaginationControls page={page} totalPaginas={totalPaginas} onPageChange={onPageChange} />
      </Group>
    );
  }

  return (
    <Group justify="space-between" wrap="wrap" gap="sm">
      <Text size="xs" c="dimmed">
        {total === 0 ? "Sin resultados para los filtros." : `Mostrando ${desde}–${hasta} de ${total} ${sustantivo}.`}
      </Text>
      {totalPaginas > 1 && <PaginationControls page={page} totalPaginas={totalPaginas} onPageChange={onPageChange} />}
    </Group>
  );
}

function PaginationControls({ page, totalPaginas, onPageChange }: { page: number; totalPaginas: number; onPageChange: (page: number) => void }) {
  // Ventana de páginas visibles alrededor de la actual (máx. 5) + primera/última siempre visibles.
  const paginas = paginasVisibles(page, totalPaginas);
  return (
    <nav aria-label="Paginación" className="flex items-center gap-1">
      <PagBtn onClick={() => onPageChange(1)} disabled={page === 1} aria-label="Primera página">
        <IconChevronsLeft size={14} />
      </PagBtn>
      <PagBtn onClick={() => onPageChange(page - 1)} disabled={page === 1} aria-label="Página anterior">
        <IconChevronLeft size={14} />
      </PagBtn>
      {paginas.map((p, i) =>
        p === "…" ? (
          <span key={`dots-${i}`} className="px-1.5 text-sm text-muted-foreground">
            …
          </span>
        ) : (
          <PagBtn key={p} onClick={() => onPageChange(p)} active={p === page} aria-label={`Página ${p}`}>
            {p}
          </PagBtn>
        )
      )}
      <PagBtn onClick={() => onPageChange(page + 1)} disabled={page === totalPaginas} aria-label="Página siguiente">
        <IconChevronRight size={14} />
      </PagBtn>
      <PagBtn onClick={() => onPageChange(totalPaginas)} disabled={page === totalPaginas} aria-label="Última página">
        <IconChevronsRight size={14} />
      </PagBtn>
    </nav>
  );
}

function PagBtn({ active, className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return (
    <button
      type="button"
      className={cn(
        "flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 text-sm transition-colors",
        "disabled:opacity-40 disabled:pointer-events-none",
        active ? "bg-multiva-600 text-white" : "text-foreground hover:bg-muted",
        className
      )}
      {...props}
    />
  );
}

/** Igual heurística que `<Pagination withEdges>` de Mantine: siempre 1 y N, ventana de ±1 alrededor de la actual, "…" en los huecos. */
function paginasVisibles(page: number, total: number): (number | "…")[] {
  const delta = 1;
  const rango: number[] = [];
  for (let i = Math.max(2, page - delta); i <= Math.min(total - 1, page + delta); i++) rango.push(i);

  const result: (number | "…")[] = [1];
  if (rango[0] > 2) result.push("…");
  result.push(...rango);
  if (rango[rango.length - 1] < total - 1) result.push("…");
  if (total > 1) result.push(total);
  return result;
}
